<template>
  <div v-if="isOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
    <div class="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl max-w-md w-full">
      <!-- Modal Header -->
      <div class="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 rounded-t-2xl text-white">
        <div class="flex justify-between items-center">
          <div class="flex items-center gap-3">
            <i class="fas fa-key text-2xl"></i>
            <h3 class="text-xl font-bold">Change Password</h3>
          </div>
          <button 
            @click="close"
            class="w-8 h-8 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-full flex items-center justify-center transition-colors"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <!-- Modal Content -->
      <form @submit.prevent="handleSubmit" class="p-6 space-y-4">
        <!-- Current Password -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Current Password
          </label>
          <div class="relative">
            <input
              v-model="form.currentPassword"
              :type="showCurrentPassword ? 'text' : 'password'"
              class="w-full px-4 py-3 bg-gray-50 dark:bg-dark-700 border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:text-white transition-colors"
              :class="{ 'border-red-500': errors.currentPassword }"
              placeholder="Enter current password"
              required
            />
            <button
              type="button"
              @click="showCurrentPassword = !showCurrentPassword"
              class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <i :class="showCurrentPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
            </button>
          </div>
          <p v-if="errors.currentPassword" class="mt-1 text-sm text-red-500">
            {{ errors.currentPassword }}
          </p>
        </div>

        <!-- New Password -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            New Password
          </label>
          <div class="relative">
            <input
              v-model="form.newPassword"
              :type="showNewPassword ? 'text' : 'password'"
              class="w-full px-4 py-3 bg-gray-50 dark:bg-dark-700 border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:text-white transition-colors"
              :class="{ 'border-red-500': errors.newPassword }"
              placeholder="Enter new password"
              required
              @input="validatePassword"
            />
            <button
              type="button"
              @click="showNewPassword = !showNewPassword"
              class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <i :class="showNewPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
            </button>
          </div>
          <p v-if="errors.newPassword" class="mt-1 text-sm text-red-500">
            {{ errors.newPassword }}
          </p>
          
          <!-- Password Strength Indicator -->
          <div v-if="form.newPassword" class="mt-2 space-y-1">
            <div class="flex gap-1">
              <div 
                v-for="i in 4" 
                :key="i" 
                class="h-1 flex-1 rounded-full transition-colors"
                :class="i <= passwordStrength ? getStrengthColor(passwordStrength) : 'bg-gray-200 dark:bg-dark-600'"
              ></div>
            </div>
            <p class="text-xs" :class="getStrengthTextColor(passwordStrength)">
              {{ getStrengthText(passwordStrength) }}
            </p>
          </div>

          <!-- Password Requirements -->
          <div class="mt-2 space-y-1 text-xs text-gray-600 dark:text-gray-400">
            <p class="flex items-center gap-1" :class="hasMinLength ? 'text-green-600 dark:text-green-400' : ''">
              <i :class="hasMinLength ? 'fas fa-check-circle' : 'far fa-circle'"></i>
              At least 8 characters
            </p>
            <p class="flex items-center gap-1" :class="hasUppercase ? 'text-green-600 dark:text-green-400' : ''">
              <i :class="hasUppercase ? 'fas fa-check-circle' : 'far fa-circle'"></i>
              One uppercase letter
            </p>
            <p class="flex items-center gap-1" :class="hasLowercase ? 'text-green-600 dark:text-green-400' : ''">
              <i :class="hasLowercase ? 'fas fa-check-circle' : 'far fa-circle'"></i>
              One lowercase letter
            </p>
            <p class="flex items-center gap-1" :class="hasNumber ? 'text-green-600 dark:text-green-400' : ''">
              <i :class="hasNumber ? 'fas fa-check-circle' : 'far fa-circle'"></i>
              One number
            </p>
            <p class="flex items-center gap-1" :class="hasSpecialChar ? 'text-green-600 dark:text-green-400' : ''">
              <i :class="hasSpecialChar ? 'fas fa-check-circle' : 'far fa-circle'"></i>
              One special character (@$!%*?&#)
            </p>
          </div>
        </div>

        <!-- Confirm New Password -->
        <div>
          <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Confirm New Password
          </label>
          <div class="relative">
            <input
              v-model="form.confirmPassword"
              :type="showConfirmPassword ? 'text' : 'password'"
              class="w-full px-4 py-3 bg-gray-50 dark:bg-dark-700 border border-gray-300 dark:border-dark-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:text-white transition-colors"
              :class="{ 'border-red-500': errors.confirmPassword }"
              placeholder="Confirm new password"
              required
            />
            <button
              type="button"
              @click="showConfirmPassword = !showConfirmPassword"
              class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <i :class="showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
            </button>
          </div>
          <p v-if="errors.confirmPassword" class="mt-1 text-sm text-red-500">
            {{ errors.confirmPassword }}
          </p>
        </div>

        <!-- Error Message -->
        <div v-if="errors.general" class="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p class="text-sm text-red-600 dark:text-red-400">
            <i class="fas fa-exclamation-circle mr-1"></i>
            {{ errors.general }}
          </p>
        </div>

        <!-- Action Buttons -->
        <div class="flex gap-3 pt-4">
          <button
            type="button"
            @click="close"
            class="flex-1 px-4 py-3 bg-gray-200 dark:bg-dark-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-dark-600 transition-colors font-medium"
            :disabled="loading"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            :disabled="loading || !isFormValid"
          >
            <i v-if="loading" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-check"></i>
            {{ loading ? 'Updating...' : 'Update Password' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useToast } from '@/composables/useToast';
import { api } from '@/services/api';

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true
  }
});

const emit = defineEmits(['close', 'success']);

const authStore = useAuthStore();
const toast = useToast();

const loading = ref(false);
const showCurrentPassword = ref(false);
const showNewPassword = ref(false);
const showConfirmPassword = ref(false);

const form = ref({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
});

const errors = ref({
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
  general: ''
});

// Password validation
const hasMinLength = computed(() => form.value.newPassword.length >= 8);
const hasUppercase = computed(() => /[A-Z]/.test(form.value.newPassword));
const hasLowercase = computed(() => /[a-z]/.test(form.value.newPassword));
const hasNumber = computed(() => /\d/.test(form.value.newPassword));
const hasSpecialChar = computed(() => /[@$!%*?&#]/.test(form.value.newPassword));

const passwordStrength = computed(() => {
  let strength = 0;
  if (hasMinLength.value) strength++;
  if (hasUppercase.value && hasLowercase.value) strength++;
  if (hasNumber.value) strength++;
  if (hasSpecialChar.value) strength++;
  return strength;
});

const isPasswordValid = computed(() => {
  return hasMinLength.value && hasUppercase.value && hasLowercase.value && hasNumber.value && hasSpecialChar.value;
});

const isFormValid = computed(() => {
  return form.value.currentPassword && 
         form.value.newPassword && 
         form.value.confirmPassword &&
         isPasswordValid.value &&
         form.value.newPassword === form.value.confirmPassword;
});

const getStrengthColor = (strength) => {
  if (strength <= 1) return 'bg-red-500';
  if (strength === 2) return 'bg-orange-500';
  if (strength === 3) return 'bg-yellow-500';
  return 'bg-green-500';
};

const getStrengthTextColor = (strength) => {
  if (strength <= 1) return 'text-red-600 dark:text-red-400';
  if (strength === 2) return 'text-orange-600 dark:text-orange-400';
  if (strength === 3) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-green-600 dark:text-green-400';
};

const getStrengthText = (strength) => {
  if (strength <= 1) return 'Weak password';
  if (strength === 2) return 'Fair password';
  if (strength === 3) return 'Good password';
  return 'Strong password';
};

const validatePassword = () => {
  errors.value.newPassword = '';
  
  if (form.value.newPassword && form.value.confirmPassword && form.value.newPassword !== form.value.confirmPassword) {
    errors.value.confirmPassword = 'Passwords do not match';
  } else {
    errors.value.confirmPassword = '';
  }
};

watch(() => form.value.confirmPassword, () => {
  if (form.value.newPassword && form.value.confirmPassword) {
    if (form.value.newPassword !== form.value.confirmPassword) {
      errors.value.confirmPassword = 'Passwords do not match';
    } else {
      errors.value.confirmPassword = '';
    }
  }
});

const handleSubmit = async () => {
  // Clear previous errors
  errors.value = {
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    general: ''
  };

  // Validate form
  if (!form.value.currentPassword) {
    errors.value.currentPassword = 'Current password is required';
    return;
  }

  if (!isPasswordValid.value) {
    errors.value.newPassword = 'Password does not meet security requirements';
    return;
  }

  if (form.value.newPassword !== form.value.confirmPassword) {
    errors.value.confirmPassword = 'Passwords do not match';
    return;
  }

  if (form.value.currentPassword === form.value.newPassword) {
    errors.value.newPassword = 'New password must be different from current password';
    return;
  }

  loading.value = true;

  try {
    // Call backend API to update password
    await api.put(`/users/${authStore.user.id}`, {
      password: form.value.newPassword,
      currentPassword: form.value.currentPassword
    });

    toast.success('Password updated successfully! Other sessions have been logged out.');
    emit('success');
    close();
  } catch (error) {
    console.error('Password change error:', error);
    
    if (error.response?.status === 401) {
      errors.value.currentPassword = 'Current password is incorrect';
    } else if (error.response?.data?.error === 'Cannot reuse any of your last 5 passwords. Please choose a different password.') {
      errors.value.newPassword = error.response.data.error;
    } else if (error.response?.data?.details) {
      // Handle validation errors from backend
      errors.value.newPassword = error.response.data.details.join(', ');
    } else {
      errors.value.general = error.response?.data?.error || 'Failed to update password. Please try again.';
    }
  } finally {
    loading.value = false;
  }
};

const close = () => {
  if (!loading.value) {
    // Reset form
    form.value = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    };
    errors.value = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      general: ''
    };
    showCurrentPassword.value = false;
    showNewPassword.value = false;
    showConfirmPassword.value = false;
    emit('close');
  }
};
</script>

